import React, { useState, useEffect, useMemo } from "react";
import { getData, postData, putData, deleteData } from "../../services/api.service";
import { toastSuccess, toastError } from "../common/Toast";
import {
  FaPaintBrush,
  FaPlus,
  FaEdit,
  FaTrash,
  FaSave,
  FaExclamationTriangle
} from "react-icons/fa";

function ColorsManager() {
  const [colors, setColors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingColor, setEditingColor] = useState(null);
  const [deletingColor, setDeletingColor] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  const [showPermanentDeleteModal, setShowPermanentDeleteModal] = useState(false);
  const [permanentDeletingColor, setPermanentDeletingColor] = useState(null);
  const [formData, setFormData] = useState({
    color_name: '',
    active: 1
  });

  useEffect(() => {
    loadColors();
  }, []);

  const loadColors = async () => {
    try {
      setLoading(true);
      const response = await getData('/api/admin/get_colors');
      setColors(response.data.colors || []);
    } catch (error) {
      console.error('Error cargando colores:', error);
      toastError('Error al cargar los colores');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      
      const dataToSend = {
        data: {
          color_name: formData.color_name,
          active: formData.active,
          user: 1 // TODO: Obtener del contexto de usuario actual
        }
      };
      
      if (editingColor) {
        dataToSend.data.id = editingColor.id;
        
        const response = await putData('/api/admin/update_color', dataToSend);
        
        if (response.response && response.response.status >= 400) {
          throw new Error(response.response.data?.msg || 'Error al actualizar');
        }
        
        setColors(prev => prev.map(color => 
          color.id === editingColor.id 
            ? { ...color, color_name: formData.color_name, active: formData.active }
            : color
        ));
        
        toastSuccess(`Color "${formData.color_name}" actualizado exitosamente`);
      } else {
        const response = await postData('/api/admin/create_color', dataToSend);
        
        if (response.response && response.response.status >= 400) {
          throw new Error(response.response.data?.msg || 'Error al crear');
        }
        
        if (response.data?.color) {
          const newColor = {
            id: response.data.color.id || response.data.id,
            color_name: formData.color_name,
            active: formData.active
          };
          setColors(prev => [newColor, ...prev]);
        }
        
        toastSuccess(`Color "${formData.color_name}" creado exitosamente`);
        loadColors().catch(() => {});
      }
      
      setShowModal(false);
      resetForm();
    } catch (error) {
      console.error('Error guardando color:', error);
      toastError(editingColor ? 'Error al actualizar el color' : 'Error al crear el color');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setEditingColor(null);
    setFormData({
      color_name: '',
      active: 1
    });
  };

  const handleEdit = (color) => {
    setEditingColor(color);
    setFormData({
      color_name: color.color_name || '',
      active: color.active !== undefined ? color.active : 1
    });
    setShowModal(true);
  };

  const handleNew = () => {
    setEditingColor(null);
    setFormData({
      color_name: '',
      active: 1
    });
    setShowModal(true);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleDelete = (color) => {
    setDeletingColor(color);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deletingColor) return;
    
    try {
      setLoading(true);
      
      // Usar PUT para marcar como inactivo en lugar de eliminar físicamente
      const dataToSend = {
        data: {
          id: deletingColor.id,
          color_name: deletingColor.color_name,
          hex_code: deletingColor.hex_code || '#000000',
          description: deletingColor.description || '',
          active: 0, // Marcar como inactivo
          user: 1 // TODO: Obtener del contexto de usuario actual
        }
      };
      
      const response = await putData('/api/admin/update_color', dataToSend);
      
      // Verificar si la respuesta es un error
      if (response.response && response.response.status >= 400) {
        throw new Error(response.response.data?.msg || 'Error al desactivar');
      }
      
      // Actualización optimista: marcar como inactivo
      setColors(prevColors => prevColors.map(color => 
        color.id === deletingColor.id ? { ...color, active: 0 } : color
      ));
      
      toastSuccess(`Color "${deletingColor.color_name}" desactivado exitosamente`);
      setShowDeleteModal(false);
      setDeletingColor(null);
    } catch (error) {
      console.error('Error desactivando color:', error);
      toastError(error.message || 'Error al desactivar el color');
    } finally {
      setLoading(false);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setDeletingColor(null);
  };

  // Eliminación PERMANENTE (DELETE físico de la base de datos)
  const handlePermanentDelete = (color) => {
    setPermanentDeletingColor(color);
    setShowPermanentDeleteModal(true);
  };

  const confirmPermanentDelete = async () => {
    if (!permanentDeletingColor) return;
    
    try {
      setLoading(true);
      
      const dataToSend = {
        id: permanentDeletingColor.id,
        user: 1
      };
      
      const response = await deleteData('/api/admin/delete_color', dataToSend);
      
      if (response?.response && response.response.status >= 400) {
        throw new Error(response.response.data?.msg || 'Error al eliminar');
      }
      
      // Actualización optimista: eliminar de la lista
      setColors(prevColors => prevColors.filter(color => color.id !== permanentDeletingColor.id));
      
      toastSuccess(`Color "${permanentDeletingColor.color_name}" eliminado permanentemente`);
      setShowPermanentDeleteModal(false);
      setPermanentDeletingColor(null);
    } catch (error) {
      console.error('Error eliminando color:', error);
      toastError(error.message || 'Error al eliminar el color definitivamente');
    } finally {
      setLoading(false);
    }
  };

  const cancelPermanentDelete = () => {
    setShowPermanentDeleteModal(false);
    setPermanentDeletingColor(null);
  };

  // Filtrar colores según el toggle - Optimizado con useMemo
  const filteredColors = useMemo(() => {
    return showInactive ? colors : colors.filter(color => color.active);
  }, [colors, showInactive]);

  return (
    <div className="colors-manager">
      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h5 className="card-title mb-0">
            <FaPaintBrush className="me-2 text-primary" />
            Gestión de Colores
          </h5>
          <div className="d-flex gap-2 align-items-center">
            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                id="showInactiveToggle"
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
              />
              <label className="form-check-label" htmlFor="showInactiveToggle">
                Mostrar inactivos
              </label>
            </div>
            <button
              className="btn btn-primary"
              onClick={handleNew}
              disabled={loading}
            >
              <FaPlus className="me-2" />
              Nuevo Color
            </button>
          </div>
        </div>

        <div className="card-body">
          {!showInactive && colors.length > filteredColors.length && (
            <div className="alert alert-info d-flex justify-content-between align-items-center mb-3">
              <div>
                <i className="fas fa-info-circle me-2"></i>
                Hay <strong>{colors.length - filteredColors.length}</strong> elemento(s) inactivo(s) oculto(s).
              </div>
              <button 
                className="btn btn-sm btn-outline-primary"
                onClick={() => setShowInactive(true)}
              >
                Mostrar todos
              </button>
            </div>
          )}

          {loading ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Cargando...</span>
              </div>
              <div className="mt-2 text-muted">Cargando colores...</div>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Nombre</th>
                    <th>Estado</th>
                    <th>Fecha Creación</th>
                    <th className="text-center">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredColors.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="text-center text-muted py-4">
                        {showInactive ? 'No hay colores registrados' : 'No hay colores activos. Activa el toggle "Mostrar inactivos" para ver colores eliminados.'}
                      </td>
                    </tr>
                  ) : (
                    filteredColors.map(color => (
                      <tr key={color.id}>
                        <td>#{color.id}</td>
                        <td>
                          <strong>{color.color_name}</strong>
                        </td>
                        <td>
                          <span className={`badge ${color.active ? 'bg-success' : 'bg-danger'}`}>
                            {color.active ? 'Activo' : 'Inactivo'}
                          </span>
                        </td>
                        <td>
                          <small className="text-muted">
                            {new Date(color.inserted_date).toLocaleDateString('es-AR')}
                          </small>
                        </td>
                        <td className="text-center">
                          <div className="btn-group" role="group">
                            <button
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => handleEdit(color)}
                              disabled={loading}
                              title="Editar color"
                            >
                              <FaEdit />
                            </button>
                            <button
                              className="btn btn-sm btn-outline-warning"
                              onClick={() => handleDelete(color)}
                              disabled={loading}
                              title={color.active ? "Inactivar color" : "Activar color"}
                            >
                              <FaTrash />
                            </button>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handlePermanentDelete(color)}
                              disabled={loading}
                              title="Eliminar definitivamente (irreversible)"
                            >
                              <FaExclamationTriangle />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modal para crear/editar color */}
      {showModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {editingColor ? 'Editar Color' : 'Nuevo Color'}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowModal(false)}
                  disabled={loading}
                ></button>
              </div>
              
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Nombre del Color *</label>
                    <input
                      type="text"
                      className="form-control"
                      name="color_name"
                      value={formData.color_name}
                      onChange={handleInputChange}
                      required
                      disabled={loading}
                      placeholder="Ej: NEGRO, AZUL MARINO, ROJO CEREZA, etc."
                    />
                    <div className="form-text">
                      Ingresa un nombre descriptivo para el color en mayúsculas
                    </div>
                  </div>
                  
                  <div className="mb-3">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        name="active"
                        checked={formData.active === 1}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          active: e.target.checked ? 1 : 0
                        }))}
                        disabled={loading}
                      />
                      <label className="form-check-label">
                        Color activo
                      </label>
                    </div>
                  </div>
                </div>
                
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowModal(false)}
                    disabled={loading}
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading || !formData.color_name.trim()}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        Guardando...
                      </>
                    ) : (
                      <>
                        <FaSave className="me-2" />
                        {editingColor ? 'Actualizar' : 'Crear'}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmación de eliminación */}
      {showDeleteModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <FaExclamationTriangle className="text-warning me-2" />
                  Confirmar Eliminación
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={cancelDelete}
                  disabled={loading}
                ></button>
              </div>
              <div className="modal-body">
                <p>¿Estás seguro que deseas eliminar el color <strong>"{deletingColor?.color_name}"</strong>?</p>
                <div className="alert alert-warning">
                  <FaExclamationTriangle className="me-2" />
                  El color será marcado como inactivo y no aparecerá en las opciones disponibles.
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={cancelDelete}
                  disabled={loading}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={confirmDelete}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Eliminando...
                    </>
                  ) : (
                    <>
                      <FaTrash className="me-2" />
                      Eliminar
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmación de eliminación PERMANENTE */}
      {showPermanentDeleteModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-danger">
              <div className="modal-header bg-danger text-white">
                <h5 className="modal-title">
                  <FaExclamationTriangle className="me-2" />
                  ⚠️ ELIMINAR DEFINITIVAMENTE
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={cancelPermanentDelete}
                  disabled={loading}
                ></button>
              </div>
              <div className="modal-body">
                <div className="alert alert-danger">
                  <strong>⚠️ ADVERTENCIA: Esta acción es IRREVERSIBLE</strong>
                </div>
                <p>¿Está COMPLETAMENTE seguro que desea eliminar permanentemente el siguiente color?</p>
                {permanentDeletingColor && (
                  <div className="alert alert-warning">
                    <strong>Color:</strong> {permanentDeletingColor.color_name}
                  </div>
                )}
                <ul className="text-danger">
                  <li>El color se eliminará <strong>permanentemente</strong> de la base de datos</li>
                  <li>NO se podrá recuperar esta información</li>
                  <li>Puede afectar pedidos y referencias existentes</li>
                </ul>
                <p className="mt-3">
                  <small className="text-muted">
                    💡 <strong>Recomendación:</strong> Si solo desea ocultar temporalmente este color,
                    use el botón de "Inactivar" en su lugar.
                  </small>
                </p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={cancelPermanentDelete}
                  disabled={loading}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={confirmPermanentDelete}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Eliminando...
                    </>
                  ) : (
                    <>
                      <FaExclamationTriangle className="me-2" />
                      ⚠️ SÍ, ELIMINAR DEFINITIVAMENTE
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ColorsManager;